import { useMemo, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { Pause, Play, Sparkles, Trash2 } from 'lucide-react'
import { api, type ReslotPlan } from '@/api/client'
import { useCampaign, useDraftCounts, useSchedule, useTargets } from '@/lib/queries'
import { Button, ConfirmDialog, ErrorState, PageHeader, Skeleton, StatusPill, errorText, useToast } from '@/ui'
import { GenerateNowDialog } from '../review/GenerateNowDialog'
import { AffiliateEditor } from './AffiliateEditor'
import { StatusCard, UpcomingPosts } from './CampaignNow'
import { CampaignPosts } from './CampaignPosts'
import { AffiliateSummary, PacingSummary, TopicPool, WritingSummary } from './CampaignSettings'
import { PacingEditor } from './PacingEditor'
import { ReslotDialog } from './ReslotDialog'
import { Section, cadence, modeLabel, unslottedLine, useCampaignMutations } from './shared'

/** /campaigns/:id: one campaign. The page reads in the order the operator
 *  works it — is it running and what goes out next (현황, 예정된 글), how the
 *  posts did (발행한 글) — with the settings beside it as read-only cards.
 *  Each card's 편집 opens its form in a drawer; pausing sits in the header,
 *  deleting at the end. */
export default function CampaignDetail() {
  const id = Number(useParams().id)
  const navigate = useNavigate()
  const toast = useToast()
  const q = useCampaign(id)
  const targets = useTargets().data ?? []
  const counts = useDraftCounts({ campaignId: id })
  // Yesterday on, so today counts on any campaign clock; the waiting lane too.
  const since = useMemo(() => {
    const d = new Date()
    return new Date(d.getFullYear(), d.getMonth(), d.getDate() - 1).toISOString()
  }, [])
  const feed = useSchedule({ source: 'campaign', campaignId: id, from: since, includeUnscheduled: true }, { enabled: id > 0 })
  const { setStatus, remove } = useCampaignMutations(id)
  const [generating, setGenerating] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [pausing, setPausing] = useState(false)
  const [editing, setEditing] = useState<'pacing' | 'affiliate' | null>(null)
  /** The re-slot dialog: opened by its button (it plans itself) or after a
   *  pacing save (with the plan already read). */
  const [reslot, setReslot] = useState<{ preview?: ReslotPlan } | null>(null)
  if (!Number.isInteger(id) || id <= 0) return <Navigate to="/campaigns" replace />

  const parent = { to: '/campaigns', label: '캠페인' }
  const c = q.data
  if (!c) {
    return (
      <>
        <PageHeader title="캠페인" parent={parent} />
        {q.error ? <ErrorState title="캠페인을 불러오지 못했다" error={q.error} onRetry={() => q.refetch()} /> : <Skeleton className="h-48" />}
      </>
    )
  }

  const n = counts.data?.counts ?? {}
  const num = (...keys: string[]) => keys.reduce((a, k) => a + (n[k] ?? 0), 0)
  const scheduled = num('approved', 'scheduled')
  const sending = num('publishing')
  // Everything that is neither waiting for a slot nor out: what is lost with
  // the campaign besides its schedule and its history.
  const drafts = Object.entries(n).reduce(
    (a, [k, v]) => (['scheduled', 'approved', 'publishing', 'published'].includes(k) ? a : a + v),
    0,
  )
  const pool = c.TopicPool ?? []
  const coupang = c.Mode === 'affiliate'
  const items = feed.data?.items ?? []

  const toggle = async () => {
    const next = c.Status === 'active' ? 'paused' : 'active'
    try {
      const r = await setStatus.mutateAsync({ id: c.ID, status: next })
      toast.success(next === 'paused' ? '멈췄다' : `다시 돌린다.${unslottedLine(r.unslotted)}`)
    } catch (e) {
      toast.error(`바꾸지 못했다: ${errorText(e)}`)
    }
  }

  // Posts already scheduled keep their old slots; after a pacing save the
  // drawer closes and the re-slot is offered when anything would move.
  const pacingSaved = async () => {
    setEditing(null)
    try {
      const preview = await api.reslotPreview(c.ID)
      if (preview.plan.length > 0) setReslot({ preview })
    } catch {
      /* the 예약 다시 배치 button can try again */
    }
  }

  return (
    <>
      <PageHeader
        parent={parent}
        docTitle={c.Name}
        title={
          <span className="inline-flex flex-wrap items-center gap-2">
            {c.Name}
            <StatusPill domain="app" status={c.Status} className="align-middle" />
          </span>
        }
        description={`${modeLabel(c.Mode)} · ${cadence(c)}`}
        actions={
          <>
            {!coupang && (
              <Button icon={Sparkles} onClick={() => setGenerating(true)}>
                AI 초안 쓰기
              </Button>
            )}
            <Button
              icon={c.Status === 'active' ? Pause : Play}
              loading={setStatus.isPending}
              onClick={() => (c.Status === 'active' ? setPausing(true) : void toggle())}
            >
              {c.Status === 'active' ? '멈춤' : '재개'}
            </Button>
          </>
        }
      />

      {c.Status === 'paused' && (
        // The attention strip's look: card ground, the state's colour only in
        // the stripe.
        <p role="status" className="relative mb-5 overflow-hidden rounded-lg border border-border bg-card py-3 pl-5 pr-4 text-table">
          <span aria-hidden className="absolute inset-y-0 left-0 w-1 bg-status-publishing" />
          멈춘 캠페인이다. 예약된 글 {scheduled}건도 나가지 않는다. 재개하면 멈춘 동안 시각이 지난 글은 슬롯 없음으로
          옮겨지니, 그 글은 다시 시각을 잡는다.
        </p>
      )}

      <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="min-w-0 space-y-5">
          <StatusCard c={c} counts={n} feed={items} loading={feed.isPending} />
          <UpcomingPosts
            campaignId={c.ID}
            feed={items}
            loading={feed.isPending}
            error={feed.error}
            onRetry={() => feed.refetch()}
            onReslot={scheduled > 0 ? () => setReslot({}) : undefined}
          />
          <CampaignPosts campaignId={c.ID} />
        </div>

        <aside aria-label="설정" className="grid min-w-0 items-start gap-5 md:grid-cols-2 xl:grid-cols-1">
          <PacingSummary c={c} targets={targets} onEdit={() => setEditing('pacing')} />
          {coupang ? <AffiliateSummary c={c} onEdit={() => setEditing('affiliate')} /> : <WritingSummary c={c} />}
          {(c.Mode === 'topic' || pool.length > 0) && <TopicPool c={c} />}
          <Section title="캠페인 삭제">
            <p className="text-table text-muted-foreground">
              멈추기만 하려면 위의 멈춤을 쓴다. 예약된 글이 있으면 지울 수 없다 — 일정에서 먼저 예약을 내린다.
            </p>
            {remove.error != null && <p className="text-table text-status-failed">{errorText(remove.error)}</p>}
            <Button variant="dangerOutline" size="sm" icon={Trash2} onClick={() => setDeleting(true)}>
              삭제
            </Button>
          </Section>
        </aside>
      </div>

      {editing === 'pacing' && <PacingEditor key={`pace-${c.ID}`} campaign={c} onClose={() => setEditing(null)} onSaved={() => void pacingSaved()} />}
      {editing === 'affiliate' && coupang && <AffiliateEditor key={`aff-${c.ID}`} campaign={c} onClose={() => setEditing(null)} />}
      {reslot && <ReslotDialog campaignId={c.ID} preview={reslot.preview} onClose={() => setReslot(null)} />}
      <ConfirmDialog
        open={pausing}
        onOpenChange={setPausing}
        title={`${c.Name} 캠페인을 멈출까`}
        description={
          scheduled > 0
            ? `멈추면 예약된 글 ${scheduled}건도 같이 멈춘다. 예약 시각이 지나도 나가지 않고, 게시 전 알림도 가지 않는다. 재개하면 그동안 시각이 지난 글은 슬롯 없음으로 옮겨진다.`
            : '멈추면 이 캠페인의 글은 예약돼 있어도 나가지 않고, 새로 승인할 수도 없다. 재개하면 다시 돌아간다.'
        }
        confirmLabel="멈춤"
        onConfirm={async () => {
          await toggle()
          setPausing(false)
        }}
      />
      {generating && <GenerateNowDialog open onClose={() => setGenerating(false)} initialCampaignId={c.ID} />}
      <ConfirmDialog
        open={deleting}
        onOpenChange={setDeleting}
        title={`${c.Name} 캠페인을 지울까`}
        description="되돌릴 수 없다. 이 캠페인의 글과 게시 기록이 함께 지워진다."
        body={
          <div className="space-y-2 text-table">
            <p className="tabular-nums">
              초안 <b>{drafts}</b>건 · 예약 <b>{scheduled}</b>건 · 게시됨 <b>{num('published')}</b>건
            </p>
            {scheduled + sending > 0 && (
              <p role="alert" className="text-status-failed">
                {scheduled > 0 ? `예약된 글이 ${scheduled}건` : `게시 중인 글이 ${sending}건`}
                {scheduled > 0 && sending > 0 && `, 게시 중인 글이 ${sending}건`} 있어 지울 수 없다. 일정에서 예약을 내리고 게시가
                끝난 뒤 지운다.
              </p>
            )}
            {num('published') > 0 && (
              <p className="text-muted-foreground">이미 올라간 글은 각 플랫폼에 그대로 남는다. 이 화면의 결과 기록만 사라진다.</p>
            )}
          </div>
        }
        confirmDisabled={scheduled + sending > 0}
        confirmLabel="삭제"
        confirmVariant="destructive"
        onConfirm={async () => {
          try {
            await remove.mutateAsync(c.ID)
            toast.success('지웠다')
            navigate('/campaigns')
          } catch {
            /* the message shows in the section */
          }
          setDeleting(false)
        }}
      />
    </>
  )
}
